import { Box, InputAdornment, InputProps, Tooltip } from '@mui/material';
import { ReactNode } from 'react';
import Iconify from 'src/components/iconify';

interface WarehouseFieldInfoProps {
  hint: string;
  /** A select draws its arrow at the right edge; the icon keeps clear of it. */
  select?: boolean;
}

/** The focusable "i" itself, with no `InputAdornment` wrapper. */
export function WarehouseInfoIcon({ hint }: { hint: string }): JSX.Element {
  return (
    <Tooltip title={hint} arrow placement="top" describeChild>
      <Box
        component="span"
        tabIndex={0}
        aria-label={hint}
        data-testid="warehouse-field-info"
        sx={{
          display: 'inline-flex',
          color: 'text.disabled',
          cursor: 'help',
        }}
      >
        <Iconify icon="eva:info-outline" width={18} />
      </Box>
    </Tooltip>
  );
}

/** An "i" at the end of a field that explains it on hover or focus. */
export default function WarehouseFieldInfo({
  hint,
  select = false,
}: WarehouseFieldInfoProps): JSX.Element {
  return (
    <InputAdornment position="end" sx={select ? { mr: 3 } : undefined}>
      <WarehouseInfoIcon hint={hint} />
    </InputAdornment>
  );
}

/** `InputProps` for a field with an info icon. */
export function fieldInfo(
  hint: string,
  select = false,
): { InputProps: Partial<InputProps> } {
  return {
    InputProps: {
      endAdornment: <WarehouseFieldInfo hint={hint} select={select} />,
    },
  };
}

/**
 * `InputProps` for a field that already has an end adornment (a unit, a
 * currency suffix, an embedded select): the icon joins it in one adornment,
 * after the existing content.
 */
export function withFieldInfo(
  existingEndAdornment: ReactNode,
  hint: string,
  select = false,
): { InputProps: Partial<InputProps> } {
  return {
    InputProps: {
      endAdornment: (
        <>
          {existingEndAdornment}
          <WarehouseFieldInfo hint={hint} select={select} />
        </>
      ),
    },
  };
}

/**
 * An Autocomplete's `params.InputProps.endAdornment` holds its clear and
 * popup buttons: the icon goes before them, in the same end adornment.
 */
export function withAutocompleteFieldInfo(
  hint: string,
  existingEndAdornment: ReactNode,
): ReactNode {
  return (
    <>
      <WarehouseFieldInfo hint={hint} />
      {existingEndAdornment}
    </>
  );
}
